import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  List,
  Datagrid,
  TextField,
  Show,
  SimpleShowLayout,
  Edit,
  SimpleForm,
  TextInput,
  DateField,
  NumberField,
  TopToolbar,
  EditButton,
  DeleteButton,
  Button,
  FunctionField,
  useRecordContext,
  useRefresh,
  useNotify,
  useDataProvider,
  SearchInput,
} from "react-admin";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Chip,
  CircularProgress,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Typography,
} from "@mui/material";
import CompareArrowsIcon from "@mui/icons-material/CompareArrows";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import RefreshIcon from "@mui/icons-material/Refresh";

const preReleaseFilters = [<SearchInput source="q_name" alwaysOn key="q" />];

interface PreReleaseImageRecord {
  imageUri: string;
  projectName?: string;
  totalFindings: number;
}

interface PreReleaseFindingRecord {
  imageUri: string;
  name: string;
  severity: string | null;
  cvssScore: number | null;
  epssScore: number | null;
  vulnerableDependencyName: string | null;
  vulnerableDependencyVersion: string | null;
  vulnerableDependencyFixVersions: string[];
  fixable: boolean | null;
}

const SEVERITY_COLORS: Record<string, string> = {
  critical: "#D32F2F",
  high: "#F57C00",
  medium: "#FBC02D",
  low: "#FFF176",
  none: "#BDBDBD",
};

export const PreReleaseList = () => (
  <List filters={preReleaseFilters} perPage={25}>
    <Datagrid rowClick="show">
      <TextField source="name" />
      <TextField source="description" />
      <NumberField source="totalFindings" />
      <DateField source="lastScannedAt" />
      <DateField source="createdAt" />
    </Datagrid>
  </List>
);

type FindingSortField =
  | "name"
  | "severity"
  | "cvssScore"
  | "epssScore"
  | "vulnerableDependencyName"
  | "vulnerableDependencyFixVersions"
  | "fixable";

const FINDING_COLUMNS: Array<{ field: FindingSortField; label: string }> = [
  { field: "name", label: "CVE" },
  { field: "severity", label: "Severity" },
  { field: "cvssScore", label: "CVSS" },
  { field: "epssScore", label: "EPSS" },
  { field: "vulnerableDependencyName", label: "Vulnerable Dependency" },
  { field: "vulnerableDependencyFixVersions", label: "Fix Versions" },
  { field: "fixable", label: "Fixable" },
];

const SEVERITY_ORDER = ["critical", "high", "medium", "low", "none"];

const dependencyLabel = (finding: PreReleaseFindingRecord) =>
  finding.vulnerableDependencyName
    ? `${finding.vulnerableDependencyName}${
        finding.vulnerableDependencyVersion
          ? `@${finding.vulnerableDependencyVersion}`
          : ""
      }`
    : "-";

// Returns a comparable value; nulls/missing values sort last in ascending order.
const getFindingSortValue = (
  finding: PreReleaseFindingRecord,
  field: FindingSortField,
): string | number => {
  switch (field) {
    case "severity": {
      const idx = SEVERITY_ORDER.indexOf(finding.severity?.toLowerCase() ?? "none");
      return idx === -1 ? SEVERITY_ORDER.length : idx;
    }
    case "cvssScore":
      return finding.cvssScore ?? -1;
    case "epssScore":
      return finding.epssScore ?? -1;
    case "vulnerableDependencyName":
      return dependencyLabel(finding).toLowerCase();
    case "vulnerableDependencyFixVersions":
      return (finding.vulnerableDependencyFixVersions ?? []).join(", ").toLowerCase();
    case "fixable":
      return finding.fixable === null || finding.fixable === undefined
        ? -1
        : finding.fixable
          ? 1
          : 0;
    default:
      return (finding[field] ?? "").toString().toLowerCase();
  }
};

const ImageFindingsTable = ({
  findings,
}: {
  findings: PreReleaseFindingRecord[];
}) => {
  // Default to highest CVSS first, matching the previous fixed order.
  const [sortField, setSortField] = useState<FindingSortField>("cvssScore");
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("desc");

  const handleSort = (field: FindingSortField) => {
    if (sortField === field) {
      setSortDirection((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortField(field);
      setSortDirection("asc");
    }
  };

  const sorted = [...findings].sort((a, b) => {
    const av = getFindingSortValue(a, sortField);
    const bv = getFindingSortValue(b, sortField);
    const cmp = av < bv ? -1 : av > bv ? 1 : 0;
    return sortDirection === "asc" ? cmp : -cmp;
  });

  return (
    <TableContainer component={Paper} variant="outlined">
      <Table size="small">
        <TableHead>
          <TableRow>
            {FINDING_COLUMNS.map((column) => (
              <TableCell
                key={column.field}
                sortDirection={sortField === column.field ? sortDirection : false}
              >
                <TableSortLabel
                  active={sortField === column.field}
                  direction={sortField === column.field ? sortDirection : "asc"}
                  onClick={() => handleSort(column.field)}
                >
                  {column.label}
                </TableSortLabel>
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {sorted.map((finding, idx) => {
            const severity = finding.severity?.toLowerCase() ?? "none";
            return (
              <TableRow key={`${finding.name}-${dependencyLabel(finding)}-${idx}`}>
                <TableCell>{finding.name}</TableCell>
                <TableCell>
                  <Chip
                    label={severity.toUpperCase()}
                    size="small"
                    sx={{
                      backgroundColor: SEVERITY_COLORS[severity] ?? "#BDBDBD",
                      fontWeight: "bold",
                    }}
                  />
                </TableCell>
                <TableCell>{finding.cvssScore ?? "-"}</TableCell>
                <TableCell>{finding.epssScore ?? "-"}</TableCell>
                <TableCell>{dependencyLabel(finding)}</TableCell>
                <TableCell>
                  {finding.vulnerableDependencyFixVersions?.length
                    ? finding.vulnerableDependencyFixVersions.join(", ")
                    : "-"}
                </TableCell>
                <TableCell>
                  {finding.fixable === null || finding.fixable === undefined
                    ? "-"
                    : finding.fixable
                      ? "Yes"
                      : "No"}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

// A pre-release's findings live in their own collection (see
// PreReleaseFinding on the backend) rather than on the PreRelease record
// itself, since a single pre-release can hold many thousands of them — far
// too many to embed on the record or load all at once on page load. Each
// accordion fetches only its own image's findings, and only once expanded.
const ImageAccordion = ({
  preReleaseId,
  image,
  refreshKey,
}: {
  preReleaseId: string;
  image: PreReleaseImageRecord;
  refreshKey?: string;
}) => {
  const dataProvider = useDataProvider() as any;
  const [expanded, setExpanded] = useState(false);
  const [findings, setFindings] = useState<PreReleaseFindingRecord[] | null>(
    null,
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // A re-scan replaces the saved findings; drop whatever this accordion
  // cached so the next expand (or the current one, if already open) re-fetches.
  useEffect(() => {
    setFindings(null);
  }, [refreshKey]);

  const loadFindings = async () => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await dataProvider.getAll("cve/pre-release-findings", {
        filter: { preRelease: preReleaseId, imageUri: image.imageUri },
        sort: { field: "cvssScore", order: "DESC" },
      });
      setFindings(data as PreReleaseFindingRecord[]);
    } catch (e: any) {
      setError(e?.message || "Failed to load CVEs for this image");
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (_: unknown, isExpanded: boolean) => {
    setExpanded(isExpanded);
    if (isExpanded && findings === null && !loading) {
      loadFindings();
    }
  };

  return (
    <Accordion
      disableGutters
      variant="outlined"
      expanded={expanded}
      onChange={handleChange}
      TransitionProps={{ unmountOnExit: true }}
    >
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Stack
          direction="row"
          spacing={2}
          alignItems="center"
          sx={{ width: "100%", pr: 2 }}
        >
          <Typography
            sx={{ fontFamily: "monospace", wordBreak: "break-all", flex: 1 }}
          >
            {image.imageUri}
          </Typography>
          {image.projectName && (
            <Typography variant="body2" color="text.secondary">
              {image.projectName}
            </Typography>
          )}
          <Chip size="small" label={`${image.totalFindings} CVEs`} />
        </Stack>
      </AccordionSummary>
      <AccordionDetails>
        {loading && (
          <Stack direction="row" spacing={1} alignItems="center" sx={{ p: 1 }}>
            <CircularProgress size={18} />
            <Typography variant="body2">Loading CVEs...</Typography>
          </Stack>
        )}
        {error && (
          <Typography variant="body2" color="error">
            {error}
          </Typography>
        )}
        {!loading && !error && findings !== null && (
          findings.length === 0 ? (
            <Typography variant="body2">No CVEs found for this image.</Typography>
          ) : (
            <ImageFindingsTable findings={findings} />
          )
        )}
      </AccordionDetails>
    </Accordion>
  );
};

const ImagesList = () => {
  const record = useRecordContext();

  if (!record || !Array.isArray(record.images) || record.images.length === 0) {
    return <div style={{ padding: "1em" }}>No images scanned.</div>;
  }

  return (
    <Box sx={{ mt: 1 }}>
      {record.images.map((image: PreReleaseImageRecord) => (
        <ImageAccordion
          key={image.imageUri}
          preReleaseId={String(record.id)}
          image={image}
          refreshKey={record.lastScannedAt}
        />
      ))}
    </Box>
  );
};

const RESCAN_ACTIVE_STATUSES = ["queued", "running"];
const RESCAN_POLL_INTERVAL_MS = 5000;

const PreReleaseShowActions = () => {
  const record = useRecordContext();
  const navigate = useNavigate();
  const dataProvider = useDataProvider() as any;
  const refresh = useRefresh();
  const notify = useNotify();
  const [submitting, setSubmitting] = useState(false);

  const scanStatus: string | undefined = record?.scanStatus;
  const isRescanning = RESCAN_ACTIVE_STATUSES.includes(scanStatus ?? "");

  // While a re-scan is queued or running, reload the record every few seconds
  // so the page picks up the new results without a manual refresh.
  useEffect(() => {
    if (!isRescanning) return;
    const intervalId = setInterval(refresh, RESCAN_POLL_INTERVAL_MS);
    return () => clearInterval(intervalId);
  }, [isRescanning, refresh]);

  // Let the user know when a re-scan they are watching finishes.
  const prevScanStatus = useRef(scanStatus);
  useEffect(() => {
    const wasRescanning = RESCAN_ACTIVE_STATUSES.includes(
      prevScanStatus.current ?? "",
    );
    if (wasRescanning && scanStatus === "completed") {
      notify("Re-scan completed", { type: "success" });
    } else if (wasRescanning && scanStatus === "failed") {
      notify(record?.scanError || "Re-scan failed", { type: "error" });
    }
    prevScanStatus.current = scanStatus;
  }, [scanStatus, record?.scanError, notify]);

  const handleRescan = async () => {
    if (!record?.id) return;
    setSubmitting(true);
    try {
      await dataProvider.rescanPreRelease(record.id);
      notify("Re-scan started", { type: "info" });
      refresh();
    } catch (e: any) {
      notify(e?.message || "Failed to start re-scan", { type: "error" });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <TopToolbar>
      <EditButton />
      <Button
        label={isRescanning ? "Re-scanning..." : "Re-scan"}
        onClick={handleRescan}
        disabled={submitting || isRescanning}
      >
        {isRescanning ? <CircularProgress size={18} /> : <RefreshIcon />}
      </Button>
      <Button
        label="Compare to Release"
        onClick={() => navigate(`/cve/pre-releases/${record?.id}/compare`)}
      >
        <CompareArrowsIcon />
      </Button>
      <DeleteButton />
    </TopToolbar>
  );
};

export const PreReleaseShow = () => (
  <Show actions={<PreReleaseShowActions />}>
    <SimpleShowLayout>
      <TextField source="name" />
      <TextField source="description" />
      <NumberField source="totalFindings" />
      <DateField source="lastScannedAt" />
      <DateField source="createdAt" />
      <DateField source="updatedAt" />
      <TextField source="scanStatus" label="Scan Status" />
      <TextField source="scanError" label="Scan Errors" />
      <FunctionField label="Images" render={() => <ImagesList />} />
    </SimpleShowLayout>
  </Show>
);

export const PreReleaseEdit = () => (
  <Edit redirect="show">
    <SimpleForm>
      <TextInput source="name" />
      <TextInput source="description" />
    </SimpleForm>
  </Edit>
);
